'use client';

import { useQuery } from '@tanstack/react-query';
import { useUnmount } from '@woonstad/helpers-hooks';
import { Filter, OverviewShape, useEffectiveSearchParamsString, useOverviewStore, useSetFilterParams } from '@woonstad/ui-public-sites';
import type { GET_WoongebouwenResponse } from '@woonstad/ui-public-sites-api';
import { useEffect, useMemo, useRef } from 'react';

const FILTER_QUERY_PRESERVED_PARAMS = new Set(['page', 'sort', 'map-open']);

/**
 * Returns only the filter-facet portion of the URL search string — page/sort/
 * map-open stripped. Used to detect "filters actually changed" so we can reset
 * pagination back to page 1.
 */
function getFilterOnlySearchString(searchParamsString: string): string {
  const params = new URLSearchParams(searchParamsString);

  for (const key of Array.from(params.keys())) {
    if (FILTER_QUERY_PRESERVED_PARAMS.has(key.split('[')[0])) {
      params.delete(key);
    }
  }

  const entries = Array.from(params.entries()).sort(([a], [b]) => a.localeCompare(b));

  return new URLSearchParams(entries).toString();
}

async function fetchWoongebouwen(searchParamsString: string): Promise<OverviewShape> {
  const params = new URLSearchParams(searchParamsString);

  if (!params.has('lang')) params.set('lang', 'nl');

  const response = await fetch(`/api/woongebouwen?${params.toString()}`);
  const json = await response.json() as GET_WoongebouwenResponse;

  if (!response.ok || !json.success || !json.data) {
    throw new Error(json.error ?? 'Failed to fetch woongebouwen');
  }

  return {
    ...json.data,
    items: [...(json.data.items ?? [])],
  };
}

export const useWoongebouwen = (_baseUrl?: string) => {
  const searchParamsString = useEffectiveSearchParamsString();
  const setFilter = useSetFilterParams();

  const query = useQuery({
    queryKey: ['woongebouwen', searchParamsString],
    queryFn: async (): Promise<OverviewShape> => fetchWoongebouwen(searchParamsString),
  });

  // Reset page when the filter portion of the URL changes.
  const filterKey = useMemo(() => getFilterOnlySearchString(searchParamsString), [searchParamsString]);
  const prevFilterKey = useRef(filterKey);
  const firstLoad = useRef(true);

  useEffect(() => {
    if (filterKey === prevFilterKey.current) return;

    if (!firstLoad.current) {
      setFilter({
        page: '',
      });
    } else {
      firstLoad.current = false;
    }

    prevFilterKey.current = filterKey;
  }, [filterKey, setFilter]);

  const setTotalActiveFilters = useOverviewStore((state) => state.setTotalActiveFilters);

  useEffect(() => {
    if (query.status === 'pending' && query.data === undefined) return;

    const totalActiveFilters = (query.data?.filters ?? []).reduce<number>((acc, filter: Filter) => {
      if (filter.options && (filter.type === 'checklist' || filter.type === 'radiolist')) {
        return acc + filter.options.filter((option) => option.selected).length;
      }

      if (filter.type === 'text') {
        return acc + (filter.value ? 1 : 0);
      }

      return acc;
    }, 0);

    setTotalActiveFilters(totalActiveFilters);
  }, [setTotalActiveFilters, query.status, query.data]);

  const setQuery = useOverviewStore((state) => state.setQuery);

  useEffect(() => {
    setQuery(query);
  }, [setQuery, query]);

  const resetStore = useOverviewStore((state) => state.resetStore);

  useUnmount(() => {
    resetStore();
  });
};
